/**
 * SERVICE WORKER REGISTRATION
 * 
 * Handles:
 * - Service worker registration
 * - Update detection
 * - Offline/online event handling
 */

import { logger } from '../utils/logger';

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
};

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // Wait for page load
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      registerValidSW(swUrl, config);
    });
  }

  // Listen for online/offline events
  window.addEventListener('online', () => {
    logger.info('🌐 Back online!');
    config?.onOnline?.();
  });

  window.addEventListener('offline', () => {
    logger.warn('📵 Offline mode');
    config?.onOffline?.();
  });
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      logger.info('✅ Service Worker registered');

      // Check for updates every hour
      setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New update available
              logger.info('🆕 New version available!');
              config?.onUpdate?.(registration);
            } else {
              // Content cached for offline use
              logger.info('📦 Content cached for offline use');
              config?.onSuccess?.(registration);
            }
          }
        };
      };
    })
    .catch((error) => {
      logger.error('❌ Service Worker registration failed:', error);
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        logger.error('❌ Service Worker unregistration failed:', error);
      });
  }
}

